
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';

export default function Register() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Información Personal
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    // Información de Ubicación
    state: '',
    city: '',
    // Información Empresarial
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    taxId: '',
    businessType: '',
    companySize: '',
    // Términos y condiciones
    acceptTerms: false,
    acceptPrivacy: false,
    acceptMarketing: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const navigate = useNavigate();

  const validateStep = (step: number) => {
    const newErrors: any = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
      if (!formData.email.trim()) newErrors.email = 'El email es requerido';
      if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email no válido';
      if (!formData.password) newErrors.password = 'La contraseña es requerida';
      if (formData.password.length < 6) newErrors.password = 'Mínimo 6 caracteres';
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Las contraseñas no coinciden';
      if (!formData.phone.trim()) newErrors.phone = 'El teléfono es requerido';
    }
    
    if (step === 2) {
      if (!formData.state) newErrors.state = 'El estado es requerido';
      if (!formData.city.trim()) newErrors.city = 'La ciudad es requerida';
    }
    
    if (step === 3) {
      if (!formData.companyName.trim()) newErrors.companyName = 'El nombre de la empresa es requerido';
      if (!formData.companyAddress.trim()) newErrors.companyAddress = 'La dirección es requerida';
      if (!formData.companyPhone.trim()) newErrors.companyPhone = 'El teléfono empresarial es requerido';
      if (!formData.taxId.trim()) newErrors.taxId = 'El RFC es requerido';
      if (!formData.businessType) newErrors.businessType = 'El tipo de negocio es requerido';
      if (!formData.companySize) newErrors.companySize = 'El tamaño de empresa es requerido';
    }
    
    if (step === 4) {
      if (!formData.acceptTerms) newErrors.acceptTerms = 'Debes aceptar los términos y condiciones';
      if (!formData.acceptPrivacy) newErrors.acceptPrivacy = 'Debes aceptar la política de privacidad';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep(4)) return;

    try {
      // Preparar datos para envío
      const submitData = new URLSearchParams();
      submitData.append('nombre_completo', formData.name);
      submitData.append('email', formData.email);
      submitData.append('telefono', formData.phone);
      submitData.append('estado', formData.state);
      submitData.append('ciudad', formData.city);
      submitData.append('nombre_empresa', formData.companyName);
      submitData.append('direccion_empresa', formData.companyAddress);
      submitData.append('telefono_empresa', formData.companyPhone);
      submitData.append('rfc', formData.taxId);
      submitData.append('tipo_negocio', formData.businessType);
      submitData.append('tamano_empresa', formData.companySize);
      submitData.append('acepta_terminos', formData.acceptTerms ? 'Si' : 'No');
      submitData.append('acepta_privacidad', formData.acceptPrivacy ? 'Si' : 'No');
      submitData.append('acepta_marketing', formData.acceptMarketing ? 'Si' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d36om9e468ku2jjobfbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      if (response.ok) {
        // Mostrar mensaje de éxito y redirigir
        alert('¡Registro exitoso! Te hemos enviado un email de confirmación.');
        navigate('/login');
      } else {
        throw new Error('Error en el registro');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Hubo un error al procesar tu registro. Por favor intenta nuevamente.');
    }
  };

  const stepTitles = [
    'Información Personal',
    'Ubicación',
    'Datos Empresariales',
    'Confirmación'
  ];

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden py-8">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=modern%20mexican%20business%20registration%20office%20with%20clean%20white%20walls%2C%20professional%20atmosphere%2C%20mexican%20flag%20colors%20accents%2C%20corporate%20environment%20with%20green%20and%20red%20details%2C%20business%20documents%20and%20forms%20on%20desk%2C%20professional%20lighting%2C%20clean%20organized%20workspace%2C%20modern%20office%20interior%20design&width=1920&height=1080&seq=business-register-bg&orientation=landscape')`
        }}
      />
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30" />
      
      {/* Registration Form Container */}
      <div className="relative z-10 w-full max-w-2xl mx-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 border-2 border-red-200">
          {/* Header */}
          <div className="text-center p-8 pb-6 border-b border-gray-200">
            <Link to="/" className="inline-block mb-4">
              <h1 className="text-3xl font-bold text-green-700" style={{ fontFamily: "Pacifico, serif" }}>
                FútbolCanchas
              </h1>
            </Link>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Registro Empresarial</h2>
            <p className="text-gray-600">Únete a la plataforma líder de canchas deportivas</p>
            
            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                {stepTitles.map((title, index) => (
                  <div
                    key={index}
                    className={`flex-1 text-center ${
                      index + 1 <= currentStep ? 'text-green-600' : 'text-gray-400'
                    }`}
                  >
                    <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center text-sm font-bold ${
                      index + 1 <= currentStep ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="text-xs mt-1 hidden sm:block">{title}</div>
                  </div>
                ))}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / 4) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} data-readdy-form className="p-8">
            {/* Step 1: Información Personal */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Información Personal</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Juan Carlos López"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="juan@empresa.com"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contraseña *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                          errors.password ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Mínimo 6 caracteres"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                      >
                        <i className={showPassword ? 'ri-eye-off-line text-gray-400' : 'ri-eye-line text-gray-400'}></i>
                      </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Contraseña *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                          errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Repite tu contraseña"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                      >
                        <i className={showConfirmPassword ? 'ri-eye-off-line text-gray-400' : 'ri-eye-line text-gray-400'}></i>
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono Personal *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+52 55 1234 5678"
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>
            )}

            {/* Step 2: Ubicación */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Información de Ubicación</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado *
                    </label>
                    <select
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8 ${
                        errors.state ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Seleccionar estado</option>
                      <option value="Aguascalientes">Aguascalientes</option>
                      <option value="Baja California">Baja California</option>
                      <option value="Baja California Sur">Baja California Sur</option>
                      <option value="Campeche">Campeche</option>
                      <option value="Chiapas">Chiapas</option>
                      <option value="Chihuahua">Chihuahua</option>
                      <option value="Ciudad de México">Ciudad de México</option>
                      <option value="Coahuila">Coahuila</option>
                      <option value="Colima">Colima</option>
                      <option value="Durango">Durango</option>
                      <option value="Estado de México">Estado de México</option>
                      <option value="Guanajuato">Guanajuato</option>
                      <option value="Guerrero">Guerrero</option>
                      <option value="Hidalgo">Hidalgo</option>
                      <option value="Jalisco">Jalisco</option>
                      <option value="Michoacán">Michoacán</option>
                      <option value="Morelos">Morelos</option>
                      <option value="Nayarit">Nayarit</option>
                      <option value="Nuevo León">Nuevo León</option>
                      <option value="Oaxaca">Oaxaca</option>
                      <option value="Puebla">Puebla</option>
                      <option value="Querétaro">Querétaro</option>
                      <option value="Quintana Roo">Quintana Roo</option>
                      <option value="San Luis Potosí">San Luis Potosí</option>
                      <option value="Sinaloa">Sinaloa</option>
                      <option value="Sonora">Sonora</option>
                      <option value="Tabasco">Tabasco</option>
                      <option value="Tamaulipas">Tamaulipas</option>
                      <option value="Tlaxcala">Tlaxcala</option>
                      <option value="Veracruz">Veracruz</option>
                      <option value="Yucatán">Yucatán</option>
                      <option value="Zacatecas">Zacatecas</option>
                    </select>
                    {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ciudad *
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                        errors.city ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Ciudad de México"
                    />
                    {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-map-pin-line text-green-600 text-lg mt-0.5"></i>
                    <div>
                      <h4 className="font-medium text-green-800 mb-1">Información de Ubicación</h4>
                      <p className="text-green-700 text-sm">
                        Esta información nos ayuda a mostrar tus canchas en los resultados de búsqueda locales 
                        y a conectarte con clientes de tu área.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Datos Empresariales */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Información Empresarial</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre de la Empresa *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                      errors.companyName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Deportes López S.A. de C.V."
                  />
                  {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dirección de la Empresa *
                  </label>
                  <input
                    type="text"
                    value={formData.companyAddress}
                    onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                      errors.companyAddress ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Av. Insurgentes Sur 1234, Col. Del Valle"
                  />
                  {errors.companyAddress && <p className="text-red-500 text-xs mt-1">{errors.companyAddress}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono Empresarial *
                    </label>
                    <input
                      type="tel"
                      value={formData.companyPhone}
                      onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                        errors.companyPhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+52 55 8765 4321"
                    />
                    {errors.companyPhone && <p className="text-red-500 text-xs mt-1">{errors.companyPhone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      RFC/ID Fiscal *
                    </label>
                    <input
                      type="text"
                      value={formData.taxId}
                      onChange={(e) => handleInputChange('taxId', e.target.value.toUpperCase())}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm ${
                        errors.taxId ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="DLO901123ABC"
                    />
                    {errors.taxId && <p className="text-red-500 text-xs mt-1">{errors.taxId}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Negocio *
                    </label>
                    <select
                      value={formData.businessType}
                      onChange={(e) => handleInputChange('businessType', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8 ${
                        errors.businessType ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Seleccionar tipo</option>
                      <option value="Centro Deportivo">Centro Deportivo</option>
                      <option value="Club Deportivo">Club Deportivo</option>
                      <option value="Canchas Particulares">Canchas Particulares</option>
                      <option value="Complejo Deportivo">Complejo Deportivo</option>
                      <option value="Academia de Fútbol">Academia de Fútbol</option>
                      <option value="Instalación Municipal">Instalación Municipal</option>
                      <option value="Otro">Otro</option>
                    </select>
                    {errors.businessType && <p className="text-red-500 text-xs mt-1">{errors.businessType}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tamaño de la Empresa *
                    </label>
                    <select
                      value={formData.companySize}
                      onChange={(e) => handleInputChange('companySize', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8 ${
                        errors.companySize ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Seleccionar tamaño</option>
                      <option value="Individual">Individual (1 persona)</option>
                      <option value="Micro">Micro (2-10 empleados)</option>
                      <option value="Pequeña">Pequeña (11-50 empleados)</option>
                      <option value="Mediana">Mediana (51-250 empleados)</option>
                      <option value="Grande">Grande (250+ empleados)</option>
                    </select>
                    {errors.companySize && <p className="text-red-500 text-xs mt-1">{errors.companySize}</p>}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-building-line text-blue-600 text-lg mt-0.5"></i>
                    <div>
                      <h4 className="font-medium text-blue-800 mb-1">Información Empresarial</h4>
                      <p className="text-blue-700 text-sm">
                        Esta información se utilizará para generar tus facturas y reportes fiscales. 
                        Asegúrate de que todos los datos sean correctos.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Confirmación */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Términos y Condiciones</h3>
                
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h4 className="font-bold text-gray-800 mb-3">Resumen de tu Registro</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Nombre:</div>
                      <div className="font-medium">{formData.name}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Email:</div>
                      <div className="font-medium">{formData.email}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Empresa:</div>
                      <div className="font-medium">{formData.companyName}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Ubicación:</div>
                      <div className="font-medium">{formData.city}, {formData.state}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer">
                      Acepto los{' '}
                      <Link to="/terminos" className="text-green-600 hover:text-green-700 underline">
                        Términos y Condiciones
                      </Link>{' '}
                      de uso de la plataforma *
                    </label>
                  </div>
                  {errors.acceptTerms && <p className="text-red-500 text-xs ml-6">{errors.acceptTerms}</p>}

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="acceptPrivacy"
                      checked={formData.acceptPrivacy}
                      onChange={(e) => handleInputChange('acceptPrivacy', e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <label htmlFor="acceptPrivacy" className="text-sm text-gray-700 cursor-pointer">
                      Acepto la{' '}
                      <Link to="/privacidad" className="text-green-600 hover:text-green-700 underline">
                        Política de Privacidad
                      </Link>{' '}
                      y el tratamiento de mis datos personales *
                    </label>
                  </div>
                  {errors.acceptPrivacy && <p className="text-red-500 text-xs ml-6">{errors.acceptPrivacy}</p>}

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="acceptMarketing"
                      checked={formData.acceptMarketing}
                      onChange={(e) => handleInputChange('acceptMarketing', e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <label htmlFor="acceptMarketing" className="text-sm text-gray-700 cursor-pointer">
                      Acepto recibir comunicaciones promocionales y ofertas especiales por email
                    </label>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-shield-check-line text-green-600 text-lg mt-0.5"></i>
                    <div>
                      <h4 className="font-medium text-green-800 mb-1">Cuenta Empresarial</h4>
                      <p className="text-green-700 text-sm">
                        Al registrarte como empresa, obtienes acceso a herramientas avanzadas de gestión, 
                        estadísticas detalladas y soporte prioritario para hacer crecer tu negocio.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <div>
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrev}
                    className="whitespace-nowrap border-2 border-gray-400 text-gray-600 hover:bg-gray-50 hover:text-gray-700"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Anterior
                  </Button>
                )}
              </div>

              <div>
                {currentStep < 4 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    className="bg-green-700 hover:bg-green-800 whitespace-nowrap border-2 border-green-700"
                  >
                    Siguiente
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className="bg-green-700 hover:bg-green-800 whitespace-nowrap border-2 border-green-700"
                  >
                    <i className="ri-check-line mr-2"></i>
                    Completar Registro
                  </Button>
                )}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="text-center p-6 pt-0 border-t border-gray-200">
            <p className="text-gray-600 text-sm">
              ¿Ya tienes cuenta?{' '}
              <Link 
                to="/login"
                className="text-green-700 hover:text-green-800 font-medium transition-colors cursor-pointer"
              >
                Inicia sesión aquí
              </Link>
            </p>
            <div className="mt-3">
              <Link 
                to="/"
                className="inline-flex items-center space-x-2 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer text-sm"
              >
                <i className="ri-arrow-left-line"></i>
                <span>Volver al inicio</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
